/**
 * MapRenderer - Main PixiJS renderer for the map viewport
 * Uses RenderTexture for smooth panning/zooming of large maps
 */

import {
    Application,
    Container,
    Graphics,
    Sprite,
    Texture,
    RenderTexture,
    FederatedPointerEvent,
} from 'pixi.js';
import { TILE_SIZE } from '@/formats/types';

export interface TileBounds {
    minX: number;
    minY: number;
    maxX: number;
    maxY: number;
}

export interface MapRendererOptions {
    /** Container element to mount the renderer */
    container: HTMLElement;
    /** Callback when mouse moves over a tile */
    onTileHover?: (x: number, y: number) => void;
    /** Callback when a tile is clicked */
    onTileClick?: (x: number, y: number, button: number) => void;
    /** Callback when dragging starts */
    onDragStart?: (x: number, y: number) => void;
    /** Callback when dragging */
    onDrag?: (x: number, y: number) => void;
    /** Callback when dragging ends */
    onDragEnd?: (x: number, y: number) => void;
    /** Callback when zoom changes */
    onZoom?: (zoom: number) => void;
    /** Callback when pan changes */
    onPan?: (panX: number, panY: number) => void;
}

export class MapRenderer {
    private app: Application;
    private container: HTMLElement;
    private mapWidth: number = 0;
    private mapHeight: number = 0;

    // Main display
    private worldContainer: Container;
    private mapSprite: Sprite;
    private objectSprite: Sprite;
    private propertyOverlay: Graphics;
    private gridLayer: Graphics;
    private cursorOverlay: Graphics;
    private cursorPreviewContainer: Container;
    private cursorPreviewSprites: Sprite[] = [];

    // Offscreen containers for building the map
    private tileContainer: Container;
    private objectContainer: Container;

    // RenderTextures for the baked map
    private tileRenderTexture: RenderTexture | null = null;
    private objectRenderTexture: RenderTexture | null = null;

    // View state
    private _zoom: number = 1;
    private _panX: number = 0;
    private _panY: number = 0;

    // Interaction state
    private isDragging: boolean = false;
    private lastMouseX: number = 0;
    private lastMouseY: number = 0;
    private isPanning: boolean = false;

    // Callbacks
    private onTileHover?: (x: number, y: number) => void;
    private onTileClick?: (x: number, y: number, button: number) => void;
    private onDragStart?: (x: number, y: number) => void;
    private onDrag?: (x: number, y: number) => void;
    private onDragEnd?: (x: number, y: number) => void;
    private onZoom?: (zoom: number) => void;
    private onPan?: (panX: number, panY: number) => void;

    // Settings
    private showGrid: boolean = true;

    // Tile cache (for the offscreen containers)
    private tileSprites: Map<string, Sprite> = new Map();
    private objectSprites: Map<string, Sprite> = new Map();

    // Dirty flag for re-baking
    private needsRebake: boolean = false;

    constructor(options: MapRendererOptions) {
        this.container = options.container;
        this.onTileHover = options.onTileHover;
        this.onTileClick = options.onTileClick;
        this.onDragStart = options.onDragStart;
        this.onDrag = options.onDrag;
        this.onDragEnd = options.onDragEnd;
        this.onZoom = options.onZoom;
        this.onPan = options.onPan;

        // Create PixiJS application
        this.app = new Application();

        // Create display layers
        this.worldContainer = new Container();
        this.mapSprite = new Sprite();
        this.objectSprite = new Sprite();
        this.propertyOverlay = new Graphics();
        this.gridLayer = new Graphics();
        this.cursorOverlay = new Graphics();
        this.cursorPreviewContainer = new Container();
        this.cursorPreviewContainer.alpha = 0.8;

        this.worldContainer.addChild(this.mapSprite);
        this.worldContainer.addChild(this.objectSprite);
        this.worldContainer.addChild(this.propertyOverlay);
        this.worldContainer.addChild(this.gridLayer);
        this.worldContainer.addChild(this.cursorOverlay);
        this.worldContainer.addChild(this.cursorPreviewContainer);

        // Create offscreen containers for building
        this.tileContainer = new Container();
        this.objectContainer = new Container();
    }

    async init(): Promise<void> {
        await this.app.init({
            resizeTo: this.container,
            backgroundColor: 0x1a1a2e,
            antialias: false,
            roundPixels: true,
            resolution: window.devicePixelRatio || 1,
            autoDensity: true,
        });

        this.container.appendChild(this.app.canvas);
        this.app.stage.addChild(this.worldContainer);

        // Set up event handling
        this.app.stage.eventMode = 'static';
        this.app.stage.hitArea = this.app.screen;

        this.app.stage.on('pointermove', this.handlePointerMove.bind(this));
        this.app.stage.on('pointerdown', this.handlePointerDown.bind(this));
        this.app.stage.on('pointerup', this.handlePointerUp.bind(this));
        this.app.stage.on('pointerupoutside', this.handlePointerUp.bind(this));
        this.app.stage.on('wheel', this.handleWheel.bind(this));

        // Handle window resize
        window.addEventListener('resize', () => {
            this.app.resize();
            this.updateView();
        });

        this.worldContainer.eventMode = 'static';
    }

    /**
     * Set map dimensions
     */
    setMapSize(width: number, height: number): void {
        this.mapWidth = width;
        this.mapHeight = height;
        this.clearTiles();

        // Create new RenderTextures
        if (this.tileRenderTexture) {
            this.tileRenderTexture.destroy();
        }
        if (this.objectRenderTexture) {
            this.objectRenderTexture.destroy();
        }

        this.tileRenderTexture = RenderTexture.create({
            width: width * TILE_SIZE,
            height: height * TILE_SIZE,
        });
        this.objectRenderTexture = RenderTexture.create({
            width: width * TILE_SIZE,
            height: height * TILE_SIZE,
        });

        this.updateGrid();
    }

    /**
     * Clear all tile sprites
     */
    clearTiles(): void {
        this.tileContainer.removeChildren();
        this.objectContainer.removeChildren();
        this.tileSprites.clear();
        this.objectSprites.clear();
        this.mapSprite.texture = Texture.EMPTY;
        this.objectSprite.texture = Texture.EMPTY;
    }

    /**
     * Set a tile sprite at position (in offscreen container)
     */
    setTileSprite(x: number, y: number, texture: Texture | null): void {
        const key = `${x},${y}`;
        let sprite = this.tileSprites.get(key);

        if (!texture) {
            if (sprite) {
                this.tileContainer.removeChild(sprite);
                this.tileSprites.delete(key);
            }
            return;
        }

        if (!sprite) {
            sprite = new Sprite(texture);
            sprite.x = x * TILE_SIZE;
            sprite.y = y * TILE_SIZE;
            this.tileContainer.addChild(sprite);
            this.tileSprites.set(key, sprite);
        } else if (sprite.texture !== texture) {
            sprite.texture = texture;
        }

        this.needsRebake = true;
    }

    /**
     * Set an object sprite at position (in offscreen container)
     */
    setObjectSprite(
        x: number,
        y: number,
        texture: Texture | null,
        pivotX: number = 0,
        pivotY: number = 0
    ): void {
        const key = `${x},${y}`;
        let sprite = this.objectSprites.get(key);

        if (!texture) {
            if (sprite) {
                this.objectContainer.removeChild(sprite);
                this.objectSprites.delete(key);
            }
            this.needsRebake = true;
            return;
        }

        if (!sprite) {
            sprite = new Sprite(texture);
            this.objectContainer.addChild(sprite);
            this.objectSprites.set(key, sprite);
        } else if (sprite.texture !== texture) {
            sprite.texture = texture;
        }

        sprite.x = x * TILE_SIZE + pivotX;
        sprite.y = y * TILE_SIZE + pivotY;
        this.needsRebake = true;
    }

    /**
     * Bake the tile and object containers to RenderTextures
     */
    bakeToTexture(): void {
        if (!this.tileRenderTexture || !this.objectRenderTexture) return;

        // Render tiles to texture
        this.app.renderer.render({
            container: this.tileContainer,
            target: this.tileRenderTexture,
            clear: true,
        });
        this.mapSprite.texture = this.tileRenderTexture;

        // Render objects to texture
        this.app.renderer.render({
            container: this.objectContainer,
            target: this.objectRenderTexture,
            clear: true,
        });
        this.objectSprite.texture = this.objectRenderTexture;

        this.needsRebake = false;
    }

    /**
     * Check and rebake if needed
     */
    rebakeIfNeeded(): void {
        if (this.needsRebake) {
            this.bakeToTexture();
        }
    }

    /**
     * Update grid overlay
     */
    private updateGrid(): void {
        this.gridLayer.clear();
        if (!this.showGrid || this.mapWidth === 0 || this.mapHeight === 0) return;

        const width = this.mapWidth * TILE_SIZE;
        const height = this.mapHeight * TILE_SIZE;

        this.gridLayer.setStrokeStyle({ width: 1, color: 0x404040, alpha: 0.5 });

        // Vertical lines
        for (let x = 0; x <= this.mapWidth; x++) {
            this.gridLayer.moveTo(x * TILE_SIZE, 0);
            this.gridLayer.lineTo(x * TILE_SIZE, height);
        }

        // Horizontal lines
        for (let y = 0; y <= this.mapHeight; y++) {
            this.gridLayer.moveTo(0, y * TILE_SIZE);
            this.gridLayer.lineTo(width, y * TILE_SIZE);
        }

        this.gridLayer.stroke();
    }

    /**
     * Update property overlays (blocked, teleport, farming, buildable, water)
     */
    updatePropertyOverlay(
        tiles: Array<Array<{ tileSprite: number; blocked: boolean; teleport: boolean; farming: boolean; buildable: boolean }>>,
        showBlocked: boolean,
        showTeleports: boolean,
        showFarming: boolean,
        showBuildable: boolean,
        showWater: boolean
    ): void {
        this.propertyOverlay.clear();

        if (!showBlocked && !showTeleports && !showFarming && !showBuildable && !showWater) return;
        if (this.mapWidth === 0 || this.mapHeight === 0) return;

        const strokeWidth = 2;
        const inset = strokeWidth / 2;

        for (let x = 0; x < this.mapWidth; x++) {
            for (let y = 0; y < this.mapHeight; y++) {
                const tile = tiles[x]?.[y];
                if (!tile) continue;

                const px = x * TILE_SIZE + inset;
                const py = y * TILE_SIZE + inset;
                const size = TILE_SIZE - strokeWidth;

                // Draw outlines for each property (can stack if multiple are true)
                if (showBlocked && tile.blocked) {
                    this.propertyOverlay.setStrokeStyle({ width: strokeWidth, color: 0xff4444, alpha: 0.9 });
                    this.propertyOverlay.rect(px, py, size, size);
                    this.propertyOverlay.stroke();
                }

                if (showTeleports && tile.teleport) {
                    this.propertyOverlay.setStrokeStyle({ width: strokeWidth, color: 0x22c55e, alpha: 0.9 });
                    this.propertyOverlay.rect(px + 2, py + 2, size - 4, size - 4);
                    this.propertyOverlay.stroke();
                }

                if (showFarming && tile.farming) {
                    this.propertyOverlay.setStrokeStyle({ width: strokeWidth, color: 0x14b8a6, alpha: 0.9 });
                    this.propertyOverlay.rect(px + 4, py + 4, size - 8, size - 8);
                    this.propertyOverlay.stroke();
                }

                if (showBuildable && tile.buildable) {
                    this.propertyOverlay.setStrokeStyle({ width: strokeWidth, color: 0xf97316, alpha: 0.9 });
                    this.propertyOverlay.rect(px + 6, py + 6, size - 12, size - 12);
                    this.propertyOverlay.stroke();
                }

                // Water tiles: maptiles1 sprite 19, and sprites 366-367
                const isWater = tile.tileSprite === 19 || tile.tileSprite === 366 || tile.tileSprite === 367;
                if (showWater && isWater) {
                    this.propertyOverlay.setStrokeStyle({ width: strokeWidth, color: 0x3b82f6, alpha: 0.9 });
                    this.propertyOverlay.rect(px + 8, py + 8, size - 16, size - 16);
                    this.propertyOverlay.stroke();
                }
            }
        }
    }

    /**
     * Update cursor overlay (brush preview)
     * @param brushTiles - Array of tiles with offset, texture, and optional pivot for objects
     */
    updateCursor(
        tileX: number,
        tileY: number,
        brushTiles: Array<{
            offsetX: number;
            offsetY: number;
            texture?: Texture | null;
            pivotX?: number;
            pivotY?: number;
        }> = []
    ): void {
        this.cursorOverlay.clear();

        // Clear preview sprites
        for (const sprite of this.cursorPreviewSprites) {
            sprite.visible = false;
        }

        if (tileX < 0 || tileY < 0 || tileX >= this.mapWidth || tileY >= this.mapHeight) {
            return;
        }

        // Check if any brush tile has a texture for preview
        const hasTexturePreview = brushTiles.some(t => t.texture);

        // Render texture previews for each brush tile
        let spriteIndex = 0;
        for (const tile of brushTiles) {
            if (!tile.texture) continue;

            const x = tileX + tile.offsetX;
            const y = tileY + tile.offsetY;

            if (x >= 0 && x < this.mapWidth && y >= 0 && y < this.mapHeight) {
                // Get or create a preview sprite
                let sprite: Sprite;
                if (spriteIndex < this.cursorPreviewSprites.length) {
                    sprite = this.cursorPreviewSprites[spriteIndex];
                } else {
                    sprite = new Sprite();
                    this.cursorPreviewContainer.addChild(sprite);
                    this.cursorPreviewSprites.push(sprite);
                }

                sprite.texture = tile.texture as Texture;
                sprite.x = x * TILE_SIZE + (tile.pivotX ?? 0);
                sprite.y = y * TILE_SIZE + (tile.pivotY ?? 0);
                sprite.visible = true;
                spriteIndex++;
            }
        }

        // Draw outline around brush area (more subtle when showing texture preview)
        const outlineAlpha = hasTexturePreview ? 0.4 : 0.8;
        this.cursorOverlay.setStrokeStyle({ width: 2, color: 0xffffff, alpha: outlineAlpha });

        if (brushTiles.length === 0) {
            // Single tile mode - no brush selected
            this.cursorOverlay.rect(
                tileX * TILE_SIZE,
                tileY * TILE_SIZE,
                TILE_SIZE,
                TILE_SIZE
            );
        } else {
            // Draw outline for each brush tile
            for (const tile of brushTiles) {
                const x = tileX + tile.offsetX;
                const y = tileY + tile.offsetY;

                if (x >= 0 && x < this.mapWidth && y >= 0 && y < this.mapHeight) {
                    this.cursorOverlay.rect(
                        x * TILE_SIZE,
                        y * TILE_SIZE,
                        TILE_SIZE,
                        TILE_SIZE
                    );
                }
            }
        }

        this.cursorOverlay.stroke();
    }

    /**
     * Set grid visibility
     */
    setShowGrid(show: boolean): void {
        this.showGrid = show;
        this.updateGrid();
    }

    /**
     * Set object visibility
     */
    setShowObjects(show: boolean): void {
        this.objectSprite.visible = show;
    }

    /**
     * Get zoom level
     */
    get zoom(): number {
        return this._zoom;
    }

    /**
     * Set zoom level
     */
    set zoom(value: number) {
        this._zoom = Math.max(0.25, Math.min(4, value));
        this.updateView();
    }

    /**
     * Get pan X
     */
    get panX(): number {
        return this._panX;
    }

    /**
     * Set pan X
     */
    set panX(value: number) {
        this._panX = value;
        this.updateView();
    }

    /**
     * Get pan Y
     */
    get panY(): number {
        return this._panY;
    }

    /**
     * Set pan Y
     */
    set panY(value: number) {
        this._panY = value;
        this.updateView();
    }

    /**
     * Update view transform
     */
    private updateView(): void {
        if (!this.worldContainer || !this.app?.stage) return;
        this.worldContainer.scale.set(this._zoom);
        this.worldContainer.x = this._panX;
        this.worldContainer.y = this._panY;
    }

    /**
     * Get the currently visible tile bounds (with padding for smooth scrolling)
     */
    getVisibleBounds(): TileBounds {
        const screenWidth = this.app?.screen?.width ?? 800;
        const screenHeight = this.app?.screen?.height ?? 600;

        // Convert screen corners to tile coordinates
        const topLeft = this.screenToTile(0, 0);
        const bottomRight = this.screenToTile(screenWidth, screenHeight);

        // Add padding of 2 tiles for smooth scrolling
        const padding = 2;

        return {
            minX: Math.max(0, topLeft.x - padding),
            minY: Math.max(0, topLeft.y - padding),
            maxX: Math.min(this.mapWidth - 1, bottomRight.x + padding),
            maxY: Math.min(this.mapHeight - 1, bottomRight.y + padding),
        };
    }

    /**
     * Center view on tile
     */
    centerOn(tileX: number, tileY: number): void {
        const screenCenterX = this.app.screen.width / 2;
        const screenCenterY = this.app.screen.height / 2;

        this._panX = screenCenterX - tileX * TILE_SIZE * this._zoom;
        this._panY = screenCenterY - tileY * TILE_SIZE * this._zoom;
        this.updateView();
    }

    /**
     * Convert screen coordinates to tile coordinates
     */
    screenToTile(screenX: number, screenY: number): { x: number; y: number } {
        const worldX = (screenX - this._panX) / this._zoom;
        const worldY = (screenY - this._panY) / this._zoom;

        return {
            x: Math.floor(worldX / TILE_SIZE),
            y: Math.floor(worldY / TILE_SIZE),
        };
    }

    /**
     * Convert tile coordinates to screen coordinates
     */
    tileToScreen(tileX: number, tileY: number): { x: number; y: number } {
        return {
            x: tileX * TILE_SIZE * this._zoom + this._panX,
            y: tileY * TILE_SIZE * this._zoom + this._panY,
        };
    }

    // Event handlers
    private handlePointerMove(event: FederatedPointerEvent): void {
        const { x, y } = event.global;

        if (this.isPanning) {
            const dx = x - this.lastMouseX;
            const dy = y - this.lastMouseY;
            this._panX += dx;
            this._panY += dy;
            this.updateView();
            this.onPan?.(this._panX, this._panY);
        } else {
            // Only update tile hover when not panning
            const tile = this.screenToTile(x, y);
            this.onTileHover?.(tile.x, tile.y);

            if (this.isDragging) {
                this.onDrag?.(tile.x, tile.y);
            }
        }

        this.lastMouseX = x;
        this.lastMouseY = y;
    }

    private handlePointerDown(event: FederatedPointerEvent): void {
        const { x, y } = event.global;
        this.lastMouseX = x;
        this.lastMouseY = y;

        // Middle mouse button or Ctrl+left click for panning
        if (event.button === 1 || (event.button === 0 && event.ctrlKey)) {
            this.isPanning = true;
            return;
        }

        const tile = this.screenToTile(x, y);

        if (event.button === 0 || event.button === 2) {
            this.isDragging = true;
            this.onDragStart?.(tile.x, tile.y);
            this.onTileClick?.(tile.x, tile.y, event.button);
        }
    }

    private handlePointerUp(event: FederatedPointerEvent): void {
        if (this.isPanning) {
            this.isPanning = false;
        }

        if (this.isDragging) {
            const { x, y } = event.global;
            const tile = this.screenToTile(x, y);
            this.onDragEnd?.(tile.x, tile.y);
            this.isDragging = false;

            // Rebake after editing session ends
            this.rebakeIfNeeded();
        }
    }

    private handleWheel(event: WheelEvent): void {
        event.preventDefault();

        // Guard against destroyed/uninitialized state
        if (!this.worldContainer?.scale || !this.app?.canvas) return;

        // Get mouse position relative to canvas
        const rect = this.app.canvas.getBoundingClientRect();
        const mouseX = event.clientX - rect.left;
        const mouseY = event.clientY - rect.top;

        // Calculate world position before zoom
        const worldX = (mouseX - this._panX) / this._zoom;
        const worldY = (mouseY - this._panY) / this._zoom;

        // Apply zoom
        const zoomDelta = event.deltaY > 0 ? 0.9 : 1.1;
        this._zoom = Math.max(0.25, Math.min(4, this._zoom * zoomDelta));

        // Adjust pan to keep mouse position stable (zoom to cursor)
        this._panX = mouseX - worldX * this._zoom;
        this._panY = mouseY - worldY * this._zoom;

        this.updateView();
        this.onZoom?.(this._zoom);
        this.onPan?.(this._panX, this._panY);
    }

    /**
     * Destroy the renderer
     */
    destroy(): void {
        if (this.tileRenderTexture) {
            this.tileRenderTexture.destroy();
        }
        if (this.objectRenderTexture) {
            this.objectRenderTexture.destroy();
        }
        this.app.destroy(true);
    }
}
